/**
 * adminLoading — drive the Shopify admin's own header loading indicator.
 *
 * This is the thin progress bar that Shopify's first-party apps (Flow, and the admin
 * itself) show at the top of the page while something is in flight. It lives OUTSIDE
 * our iframe, in the admin chrome, which is the whole reason it is worth using: during
 * the boot window our iframe has nothing on screen yet, so the only surface that can
 * tell the merchant "this is working" is Shopify's.
 *
 * API: `shopify.loading(true|false)` on the App Bridge global.
 * https://shopify.dev/docs/api/app-home/apis/user-interface-and-interactions/loading-api
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ONE HAZARD, straight from the API docs
 * ─────────────────────────────────────────────────────────────────────────────
 * "The indicator appears at the top of the admin and persists until you explicitly
 * stop it."
 *
 * There is no timeout, no auto-clear on navigation, and no way to query the current
 * state. A single leaked `true` leaves a permanently animating bar in the merchant's
 * admin — which reads as "this app is broken/hung" and is strictly worse than never
 * having shown it. Everything below exists to make a leak impossible:
 *
 *   1. REFCOUNT, not a boolean. Several gates overlap during boot (router → auth →
 *      shop → route chunk → campaigns). A plain boolean means whichever gate finishes
 *      first switches the bar off while three others are still waiting.
 *   2. DEBOUNCED STOP. Those gates hand over in the same tick — one unmounts as the
 *      next mounts. Stopping synchronously would strobe the bar on every handoff.
 *   3. HARD DEADLINE. If the refcount somehow never returns to zero (an exception
 *      between mount and unmount, a component that never unmounts), the deadline
 *      force-stops the bar. A missing indicator is a cosmetic loss; a stuck one is a
 *      support ticket.
 *
 * ⚠️ Do NOT "simplify" this to `shopify.loading(isLoading)` inside a component. That
 * is the boolean form, and it re-creates hazard 1 and 2 together.
 */

import { useEffect } from "react";

/** Overlapping owners of the indicator. The bar is on iff this is > 0. */
let owners = 0;

/** Pending debounced stop, so a handoff between two gates does not strobe. */
let stopTimer = null;

/** Absolute fail-safe — see hazard 3. */
let deadlineTimer = null;

/**
 * True while the claim inherited from index.html's inline script is still held.
 *
 * ⚠️ This claim has NO natural owner. Every other claim belongs to a mounted component
 * that releases it on unmount; this one was made before React existed, by a script that
 * cannot release it. Left alone it would pin `owners` at 1 forever and the bar would
 * stay up until the deadline fired — on EVERY load. So the first component claim hands
 * over: it releases this one as it takes its own, which keeps the count correct without
 * ever dropping to zero in between (no flicker).
 *
 * In practice the handover is guaranteed: App.jsx's router gate renders `<BootShell/>`
 * on its very first render, so a component claim always arrives. The deadline remains
 * as the backstop for the pathological case where it somehow does not.
 */
let bootClaimHeld = false;

/**
 * How long to wait before honouring a stop. Long enough to cover an unmount/mount
 * handoff in the same frame, short enough that the bar does not linger visibly after
 * the page is usable.
 */
const STOP_DEBOUNCE_MS = 120;

/**
 * Longest the bar may stay up, whatever the refcount says.
 *
 * Deliberately generous: a Gadget query measures 320 ms p50 but 2.1 s p90, and a cold
 * CDN edge adds ~1.75 s (docs/architecture/admin-load-time.md), so a legitimately slow
 * boot can run several seconds and must not be cut off mid-flight. This is a safety
 * net for a bug, not a UX timer.
 */
const MAX_VISIBLE_MS = 20000;

/** The App Bridge global is absent outside the embedded admin (tests, standalone). */
function loadingApi() {
  if (typeof window === "undefined") return null;
  try {
    const fn = window.shopify?.loading;
    return typeof fn === "function" ? fn.bind(window.shopify) : null;
  } catch {
    return null;
  }
}

function applyOn() {
  const api = loadingApi();
  if (!api) return;
  try {
    api(true);
  } catch {
    // App Bridge not ready, or the host rejected it. The indicator is decoration —
    // never let it break a boot.
  }
}

function applyOff() {
  const api = loadingApi();
  if (!api) return;
  try {
    api(false);
  } catch {
    /* see applyOn */
  }
}

function clearTimers() {
  if (stopTimer !== null) {
    clearTimeout(stopTimer);
    stopTimer = null;
  }
  if (deadlineTimer !== null) {
    clearTimeout(deadlineTimer);
    deadlineTimer = null;
  }
}

/**
 * Claim the indicator. Every call MUST be paired with exactly one `stopAdminLoading()`.
 * Prefer `useAdminLoading()` below, which pairs them for you.
 */
export function startAdminLoading() {
  // A pending stop means a previous owner just released and a new one arrived inside
  // the debounce window — the handoff case. Cancel the stop rather than restarting.
  if (stopTimer !== null) {
    clearTimeout(stopTimer);
    stopTimer = null;
  }

  // Hand over from the ownerless boot claim (see `bootClaimHeld`). Releasing it in the
  // same call that takes the new claim means the count never passes through zero, so
  // the bar does not blink at the handover.
  if (bootClaimHeld) {
    bootClaimHeld = false;
    owners = Math.max(0, owners - 1);
  }

  owners += 1;
  if (owners > 1) return; // already on

  applyOn();

  if (deadlineTimer === null) {
    deadlineTimer = setTimeout(() => {
      deadlineTimer = null;
      bootClaimHeld = false;
      owners = 0;
      applyOff();
    }, MAX_VISIBLE_MS);
  }
}

/** Release one claim. The bar goes off when the last owner releases. */
export function stopAdminLoading() {
  owners = Math.max(0, owners - 1);
  if (owners > 0) return;
  if (stopTimer !== null) return;

  stopTimer = setTimeout(() => {
    stopTimer = null;
    // Re-check: a new owner may have claimed it since this was scheduled.
    if (owners > 0) return;
    bootClaimHeld = false;
    clearTimers();
    applyOff();
  }, STOP_DEBOUNCE_MS);
}

/**
 * Adopt the claim made by the inline script in index.html.
 *
 * That script turns the bar on during the pre-React window — the seconds when the
 * iframe is genuinely blank and no component exists to represent the wait. It cannot
 * import this module, so it hands over through `window.__nsAdminLoadingOwned`.
 * Calling this makes the module the sole owner, so the normal refcount takes it from
 * there and the inline script's own fail-safe stands down.
 *
 * Safe to call more than once; only the first call adopts.
 */
export function adoptBootLoading() {
  if (typeof window === "undefined") return;
  if (!window.__nsAdminLoadingOwned) return;

  // Stand the inline script's fail-safe down — from here the module owns the bar.
  window.__nsAdminLoadingOwned = false;

  // NOT startAdminLoading(): this claim has no component to release it, so it is
  // tracked separately and handed over to the first real claim. See `bootClaimHeld`.
  if (bootClaimHeld) return;
  bootClaimHeld = true;
  owners += 1;

  // The bar is already on (the inline script turned it on before React existed), but
  // re-asserting is harmless and covers the case where App Bridge initialised late.
  applyOn();

  if (deadlineTimer === null) {
    deadlineTimer = setTimeout(() => {
      deadlineTimer = null;
      bootClaimHeld = false;
      owners = 0;
      applyOff();
    }, MAX_VISIBLE_MS);
  }
}

/**
 * Hold the indicator for as long as the calling component is mounted.
 *
 * Deliberately takes no arguments and has no condition: the component's own existence
 * IS the condition. Every boot gate already renders a dedicated component while it
 * waits (`BootShell`, `RouteLoader`), so mounting one of those is exactly the signal.
 *
 * StrictMode note: setup → cleanup → setup runs start/stop/start. The stop is debounced
 * and the second start cancels it, so the bar does not flicker in development. There is
 * deliberately no ref guard here — a ref guard plus a cleanup function is the pair that
 * deadlocks under StrictMode (.claude/rules/frontend.md), and it would also break the
 * refcount by skipping starts while still running stops.
 */
export function useAdminLoading() {
  useEffect(() => {
    startAdminLoading();
    return stopAdminLoading;
  }, []);
}
